import { useEffect, useMemo, useState } from 'react';
import { useExternalsStore } from '../store/externals-store.js';
import { api } from '../lib/api.js';

/** Web-URL der Browser-Seite einer Web-Intercom (analog zum Panel-Share-Link). */
function intercomUrl(deviceId: string): string {
  const path = `/intercom/${deviceId}`;
  if (typeof window === 'undefined') return path;
  const override = (import.meta.env.VITE_PANEL_BASE_URL as string | undefined)?.replace(/\/$/, '');
  if (override) return `${override}${path}`;
  const { protocol, hostname, port } = window.location;
  const isDev = hostname === 'localhost' || hostname === '127.0.0.1' || /^192\.168\./.test(hostname);
  if (isDev) return `${protocol}//${hostname}:5174${path}`;
  const panelHost = hostname.startsWith('panel.') ? hostname : `panel.${hostname}`;
  const portPart = port && port !== '80' && port !== '443' ? `:${port}` : '';
  return `${protocol}//${panelHost}${portPart}${path}`;
}

/**
 * Verwaltung externer Ports (live) und externer Geräte (gepaarte mic+spk).
 * Ports kommen aus dem LiveKit-Webhook und werden hier nur angezeigt;
 * Geräte werden hier gepaart und gespeichert.
 */
export function ExternalsPage(): JSX.Element {
  const bind = useExternalsStore((s) => s.bind);
  const ports = useExternalsStore((s) => s.ports);
  const devices = useExternalsStore((s) => s.devices);
  const loading = useExternalsStore((s) => s.loading);
  const lastError = useExternalsStore((s) => s.lastError);
  const createDevice = useExternalsStore((s) => s.createDevice);
  const updateDevice = useExternalsStore((s) => s.updateDevice);
  const removeDevice = useExternalsStore((s) => s.removeDevice);

  useEffect(() => bind(), [bind]);

  const micPorts = useMemo(() => ports.filter((p) => p.kind === 'ext-mic'), [ports]);
  const spkPorts = useMemo(() => ports.filter((p) => p.kind === 'ext-spk'), [ports]);
  const pgmPorts = useMemo(() => ports.filter((p) => p.kind === 'pgm'), [ports]);

  return (
    <div className="p-6 space-y-8">
      <header>
        <h1 className="text-2xl font-semibold text-text">Externe Ports & Geräte</h1>
        <p className="text-sm text-text-mute mt-1">
          Audio-Strecken, die der Desktop in den LiveKit-Room einspielt
          (PGM, Riedel-Mic) und Lautsprecher-Outputs für Rückkanäle.
        </p>
        {lastError && (
          <div className="mt-3 text-sm text-red-400">Fehler: {lastError}</div>
        )}
      </header>

      <section>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-sm uppercase tracking-wider text-text-mute">
            Live-Ports {loading && '…'}
          </h2>
          <button
            className="text-xs px-2 py-1 rounded bg-surface-2 hover:bg-surface-3 text-text"
            onClick={async () => {
              try {
                await api.post('/api/v1/ports/resync', {});
              } catch (e) {
                console.warn('[resync] failed', e);
              }
            }}
            title="Reconcile gegen LiveKit – räumt Karteileichen auf"
          >
            Resync
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <PortColumn title="PGM" items={pgmPorts} empty="Kein PGM-Track im Room." />
          <PortColumn title="Ext-Mic" items={micPorts} empty="Kein ext-Mic publishen." />
          <PortColumn title="Ext-Speaker" items={spkPorts} empty="Kein ext-Speaker registriert." />
        </div>
        <p className="text-xxs text-text-mute mt-2">
          Tipp: Speaker-Ports erscheinen automatisch, sobald ein Desktop einen
          ext-spk Slot aktiviert hat (auch ohne eigenen Publish).
        </p>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm uppercase tracking-wider text-text-mute">
            Externe Geräte (Pairing)
          </h2>
          <button
            className="text-xs px-2.5 py-1.5 rounded bg-accent text-bg font-medium hover:opacity-90"
            onClick={async () => {
              const name = window.prompt(
                'Name der Web-Intercom (bidirektional, läuft im Browser ohne Desktop):',
                'Web-Intercom',
              );
              if (!name || !name.trim()) return;
              const dev = await createDevice({ name: name.trim(), web: true });
              const url = intercomUrl(dev.id);
              if (window.confirm(`Angelegt. Link öffnen?\n${url}`)) window.open(url, '_blank');
            }}
            title="Bidirektionale externe Intercom im Browser anlegen (ext-mic + ext-spk automatisch)"
          >
            + Web-Intercom
          </button>
        </div>
        <DeviceEditor
          ports={ports}
          onCreate={async (name, mic, spk) => {
            await createDevice({
              name,
              ...(mic ? { micPortKey: mic } : {}),
              ...(spk ? { spkPortKey: spk } : {}),
            });
          }}
        />
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
          {devices.length === 0 && (
            <div className="text-sm text-text-mute">Noch keine Geräte angelegt.</div>
          )}
          {devices.map((d) => (
            <DeviceRow
              key={d.id}
              device={d}
              ports={ports}
              onUpdate={(patch) => updateDevice(d.id, patch)}
              onRemove={() => removeDevice(d.id)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

function PortColumn({
  title,
  items,
  empty,
}: {
  title: string;
  items: { key: string; label: string; online: boolean; identity: string }[];
  empty: string;
}): JSX.Element {
  return (
    <div className="rounded-btn border border-border bg-surface p-3">
      <div className="text-sm font-semibold text-text mb-2">{title}</div>
      {items.length === 0 ? (
        <div className="text-xs text-text-mute">{empty}</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((p) => (
            <li key={p.key} className="flex items-center gap-2">
              <span
                className={[
                  'w-2 h-2 rounded-full',
                  p.online ? 'bg-green-400' : 'bg-gray-500',
                ].join(' ')}
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-text truncate">{p.label}</div>
                <div className="text-xxs text-text-mute truncate">{p.identity}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DeviceEditor({
  ports,
  onCreate,
}: {
  ports: { key: string; label: string; kind: string; online: boolean }[];
  onCreate: (name: string, mic?: string, spk?: string) => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState('');
  const [mic, setMic] = useState('');
  const [spk, setSpk] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const micOpts = ports.filter((p) => p.kind === 'ext-mic');
  const spkOpts = ports.filter((p) => p.kind === 'ext-spk');
  return (
    <div className="rounded-btn border border-border bg-surface p-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
      <label className="text-xs text-text-mute">
        Name
        <input
          className="input mt-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z. B. Riedel-A"
        />
      </label>
      <label className="text-xs text-text-mute">
        Mic-Port (ext-mic)
        <select className="input mt-1" value={mic} onChange={(e) => setMic(e.target.value)}>
          <option value="">— optional —</option>
          {micOpts.map((p) => (
            <option key={p.key} value={p.key}>
              {p.online ? '● ' : '○ '}
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <label className="text-xs text-text-mute">
        Speaker-Port (ext-spk)
        <select className="input mt-1" value={spk} onChange={(e) => setSpk(e.target.value)}>
          <option value="">— optional —</option>
          {spkOpts.map((p) => (
            <option key={p.key} value={p.key}>
              {p.online ? '● ' : '○ '}
              {p.label}
            </option>
          ))}
        </select>
      </label>
      <button
        className="btn-primary"
        disabled={busy || name.trim().length === 0}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            await onCreate(name.trim(), mic || undefined, spk || undefined);
            setName('');
            setMic('');
            setSpk('');
          } catch (e) {
            setErr((e as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        Gerät anlegen
      </button>
      {err && <div className="text-xs text-red-400 col-span-full">{err}</div>}
    </div>
  );
}

function DeviceRow({
  device,
  ports,
  onUpdate,
  onRemove,
}: {
  device: {
    id: string;
    name: string;
    micPortKey?: string;
    spkPortKey?: string;
    pgmPortKey?: string;
    pgmVolume?: number;
    web?: boolean;
  };
  ports: { key: string; label: string; kind: string; online: boolean }[];
  onUpdate: (patch: {
    name?: string;
    micPortKey?: string | null;
    spkPortKey?: string | null;
    pgmPortKey?: string | null;
    pgmVolume?: number | null;
  }) => Promise<unknown>;
  onRemove: () => Promise<void>;
}): JSX.Element {
  const [name, setName] = useState(device.name);
  const [mic, setMic] = useState(device.micPortKey ?? '');
  const [spk, setSpk] = useState(device.spkPortKey ?? '');
  const [pgm, setPgm] = useState(device.pgmPortKey ?? '');
  const [pgmVol, setPgmVol] = useState(device.pgmVolume ?? 100);
  const [err, setErr] = useState<string | null>(null);
  const dirty =
    name !== device.name ||
    mic !== (device.micPortKey ?? '') ||
    spk !== (device.spkPortKey ?? '') ||
    pgm !== (device.pgmPortKey ?? '') ||
    pgmVol !== (device.pgmVolume ?? 100);
  return (
    <div className="rounded-btn border border-border bg-surface p-3 space-y-2">
      <div className="flex items-center gap-2">
        <input className="input flex-1" value={name} onChange={(e) => setName(e.target.value)} />
        {device.web ? (
          <a
            href={intercomUrl(device.id)}
            target="_blank"
            rel="noreferrer"
            className="text-xxs uppercase tracking-wider px-2 py-1 rounded bg-accent/15 text-accent hover:bg-accent/25 shrink-0"
            title="Web-Intercom-Seite öffnen"
          >
            Web · Öffnen
          </a>
        ) : null}
        <button
          className="btn-ghost text-red-400"
          onClick={async () => {
            if (!confirm(`Gerät „${device.name}" löschen?`)) return;
            try {
              await onRemove();
            } catch (e) {
              setErr((e as Error).message);
            }
          }}
        >
          Löschen
        </button>
      </div>
      {device.web ? (
        <div className="text-xxs text-text-mute">
          Mic + Speaker werden automatisch betrieben, sobald die Web-Intercom-Seite verbunden ist.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <select className="input" value={mic} onChange={(e) => setMic(e.target.value)}>
            <option value="">— Mic ungesetzt —</option>
            {ports
              .filter((p) => p.kind === 'ext-mic')
              .map((p) => (
                <option key={p.key} value={p.key}>
                  {p.online ? '● ' : '○ '}
                  {p.label}
                </option>
              ))}
          </select>
          <select className="input" value={spk} onChange={(e) => setSpk(e.target.value)}>
            <option value="">— Speaker ungesetzt —</option>
            {ports
              .filter((p) => p.kind === 'ext-spk')
              .map((p) => (
                <option key={p.key} value={p.key}>
                  {p.online ? '● ' : '○ '}
                  {p.label}
                </option>
              ))}
          </select>
        </div>
      )}
      {/* PGM ans Gerät: Quelle + Lautstärke (leer = hört kein Programm). */}
      <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
        <select className="input" value={pgm} onChange={(e) => setPgm(e.target.value)}>
          <option value="">— Kein PGM auf dem Gerät —</option>
          {ports
            .filter((p) => p.kind === 'pgm')
            .map((p) => (
              <option key={p.key} value={p.key}>
                {p.online ? '● ' : '○ '}
                PGM: {p.label}
              </option>
            ))}
        </select>
        <div className="flex items-center gap-2 min-w-[140px]">
          <input
            type="range"
            min={0}
            max={100}
            value={pgmVol}
            disabled={!pgm}
            onChange={(e) => setPgmVol(parseInt(e.target.value, 10))}
            className="flex-1"
          />
          <span className="text-xxs text-text-mute tabular-nums w-9 text-right">{pgmVol}%</span>
        </div>
      </div>
      {dirty && (
        <div className="flex gap-2">
          <button
            className="btn-primary text-xs"
            onClick={async () => {
              setErr(null);
              try {
                await onUpdate({
                  name,
                  micPortKey: mic ? mic : null,
                  spkPortKey: spk ? spk : null,
                  pgmPortKey: pgm ? pgm : null,
                  pgmVolume: pgm ? pgmVol : null,
                });
              } catch (e) {
                setErr((e as Error).message);
              }
            }}
          >
            Speichern
          </button>
          <button
            className="btn-ghost text-xs"
            onClick={() => {
              setName(device.name);
              setMic(device.micPortKey ?? '');
              setSpk(device.spkPortKey ?? '');
              setPgm(device.pgmPortKey ?? '');
              setPgmVol(device.pgmVolume ?? 100);
            }}
          >
            Verwerfen
          </button>
        </div>
      )}
      {err && <div className="text-xs text-red-400">{err}</div>}
    </div>
  );
}
